import { standardSchemaResolver } from '@hookform/resolvers/standard-schema'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import { Alert, AlertTitle, Stack, Typography } from '@mui/material'
import { UpdateUserBody, updateUserBodySchema } from '@shared/dto/users.dto'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { TextInput } from '../../components/forms/TextInput'
import { LoaderButton } from '../../components/LoaderButton'
import { useAuthStore } from '../../hooks/useAuthStore'
import { useUserUpdateMutation } from '../../hooks/useMutations/useUsersMutations'

export default function CompleteProfile() {
  const navigate = useNavigate()
  const { userId } = useAuthStore()
  const { t } = useTranslation()
  const [alertData, setAlertData] = useState<{
    title: string
    description: string
  } | null>(null)

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors }
  } = useForm<UpdateUserBody>({
    defaultValues: {
      name: '',
      lastName: ''
    },
    resolver: standardSchemaResolver(updateUserBodySchema)
  })

  const updateUserMutation = useUserUpdateMutation()

  const onSubmit = async (user: UpdateUserBody) => {
    if (!userId) return

    updateUserMutation.mutate(
      { user, userId },
      {
        onSuccess: async () => {
          navigate('/companies')
        },
        onError: () => {
          setAlertData({
            title: t('error.unexpected.title'),
            description: t('error.unexpected.description')
          })
        }
      }
    )
  }

  const name = watch('name')
  const lastName = watch('lastName')

  return (
    <Stack
      direction='column'
      spacing='1rem'
      sx={{
        width: '37.875rem',
        height: '37.875rem',
        padding: '2.5rem',
        borderRadius: '1.5rem',
        backgroundColor: 'background.default'
      }}
    >
      <Alert severity='success' sx={{ padding: '0.375rem 1rem' }}>
        <AlertTitle sx={{ fontWeight: 700 }}>
          {t('complete_profile.account_confirmed')}
        </AlertTitle>
      </Alert>
      <Typography variant='h4'>{t('complete_profile.title')}</Typography>
      <Typography variant='h6' color='text.secondary'>
        {t('complete_profile.subtitle')}
      </Typography>

      <TextInput
        name='name'
        register={register}
        errors={errors}
        label={t('complete_profile.name')}
      />
      <TextInput
        name='lastName'
        register={register}
        errors={errors}
        label={t('complete_profile.last_name')}
      />
      <LoaderButton
        onClick={handleSubmit(onSubmit)}
        disabled={!name || !lastName || updateUserMutation.isPending}
        loading={updateUserMutation.isPending}
        label={
          updateUserMutation.isPending
            ? t('common.saving')
            : t('common.continue')
        }
        startIcon={<ChevronLeftIcon />}
        sx={{ textTransform: 'none' }}
      />
      <Alert
        severity='error'
        sx={{ visibility: alertData ? 'visible' : 'hidden' }}
      >
        <AlertTitle>{alertData?.title || ''}</AlertTitle>
        {alertData?.description || ''}
      </Alert>
    </Stack>
  )
}
